/* eslint-disable @typescript-eslint/no-unused-expressions */
import React, {
  useState,
  ReactNode,
  useMemo,
  memo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import Chip from "@mui/material/Chip";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { SxProps, Theme } from "@mui/material/styles";
import { FilterChipActionButtons } from "./FilterChipActionButtons";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { RotatingBorder } from "../RotatingBorder";

interface FilterChipProps {
  /**
   * The filter label (e.g. "Products", "Categories", etc.)
   */
  label: string;

  /**
   * Function to determine how many items are selected
   */
  getSelectedCount: () => number;

  /**
   * Optional custom label renderer
   */
  renderLabel?: (label: string, count: number) => ReactNode;

  /**
   * Children to render inside the popover (the actual filter component)
   */
  children: ReactNode;

  /**
   * Optional function to handle chip deletion (removes the filter)
   */
  onDelete?: () => void;

  /**
   * Optional function to handle chip deletion (removes the filter)
   */
  addTitleClearButton?: boolean;

  /**
   * Add a cancel (X) button to the title that closes the popover
   */
  addTitleCancelButton?: boolean;

  /**
   * Optional ID for the filter chip
   */
  id?: string;

  /**
   * Optional data-testid forwarded to the chip root for e2e targeting
   */
  dataTestId?: string;

  /**
   * Custom styles for the Paper component inside the popover
   */
  paperStyles?: SxProps<Theme>;

  highlighted?: boolean;

  /**
   * Optional actions to render in the title bar (e.g. upload/download buttons)
   */
  titleActions?: ReactNode;

  /**
   * Optional adornment to render right next to the title (e.g. help icon)
   */
  titleAdornment?: ReactNode;

  /**
   * Options to customize the popover behavior and actions
   */
  popoverOptions?: {
    /**
     * The title of the popover
     */
    title?: string;

    /**
     * Callback fired when the popover opens
     */
    onOpen?: () => void;

    /**
     * Callback fired when the popover closes
     */
    onClose?: () => void;

    /**
     * Callback fired when the Clear button is clicked
     * Typically used to reset filters to their default state
     */
    onClearButtonClick?: () => void;

    /**
     * Callback fired when the Cancel button is clicked
     * Typically used to revert changes made in the filter
     */
    onCancelButtonClick?: () => void;

    /**
     * Callback fired when the Apply button is clicked
     * Typically used to confirm and apply filter changes
     */
    onApplyButtonClick?: () => void;

    /**
     * Whether to show the action buttons (Clear, Cancel, Apply)
     * @default false
     */
    disableActionButtons?: boolean;

    /**
     * Whether to disable the Clear button
     * @default false
     */
    disableClearButton?: boolean;

    /**
     * Whether to disable the Cancel button
     * @default false
     */
    disableCancelButton?: boolean;

    /**
     * Whether to disable the Apply button
     * @default false
     */
    disableApplyButton?: boolean;
  };
}

// Interface to expose the methods to the parent component
export interface FilterChipRef {
  closePopover: () => void;
}

// Memoize static components to prevent unnecessary re-renders
const PopoverTitle = memo(
  ({
    title,
    addTitleClearButton,
    addTitleCancelButton,
    titleActions,
    titleAdornment,
    id,
    onDelete,
    onCancel,
  }: {
    title: string;
    addTitleClearButton: boolean;
    addTitleCancelButton: boolean;
    titleActions?: ReactNode;
    titleAdornment?: ReactNode;
    id: string;
    onDelete?: () => void;
    onCancel?: () => void;
  }) => (
    <Stack direction="row" alignItems="center" mb={1}>
      <Typography variant="subtitle1" fontWeight={500}>
        {title}
      </Typography>
      {titleAdornment}
      <Box ml="auto" display="flex" alignItems="center" gap={1}>
        {titleActions}
        {addTitleClearButton && (
          <Button id={`${id}-clear-btn`} onClick={onDelete}>
            Clear
          </Button>
        )}
        {addTitleCancelButton && (
          <IconButton size="small" id={`${id}-cancel-btn`} onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Stack>
  )
);

// Pre-configure default paper styles for the popper content
const defaultPaperStyles = {
  width: 400,
  p: 2,
  boxShadow: 3,
  mt: 2,
};

const renderIncludedCountLabel = (label: string, count: number) => {
  if (count === 0) {
    return (
      <Typography component="span" color="textSecondary">
        Select {label}
      </Typography>
    );
  }

  if (count === 1) {
    return (
      <Stack direction="row">
        <Typography component="span" color="textSecondary">
          {label}:
        </Typography>
        <Typography component="span" pl={0.5}>
          1 included
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row">
      <Typography component="span" color="textSecondary">
        {label}:
      </Typography>
      <Typography component="span" pl={0.5}>
        {count} included
      </Typography>
    </Stack>
  );
};

/**
 * A generic reusable filter chip component that displays the number of selected items
 * and opens a popover with the provided filter component when clicked.
 */
export const FilterChip = memo(
  forwardRef<FilterChipRef, FilterChipProps>(
    (
      {
        label,
        getSelectedCount,
        renderLabel,
        children,
        onDelete,
        paperStyles,
        popoverOptions,
        addTitleClearButton = false,
        addTitleCancelButton = false,
        id = `filter-chip-${label.toLowerCase().replace(/\s+/g, "-")}`,
        dataTestId,
        highlighted,
        titleActions,
        titleAdornment,
      },
      ref
    ) => {
      const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
      const isOpen = Boolean(anchorEl);

      const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
          popoverOptions?.onOpen && popoverOptions.onOpen();
        },
        [popoverOptions]
      );

      const handleClose = useCallback(() => {
        setAnchorEl(null);
        popoverOptions?.onClose && popoverOptions.onClose();
      }, [popoverOptions]);

      const handleClickAway = useCallback(
        (event: MouseEvent | TouchEvent) => {
          // Don't close if clicking inside a MUI Dialog
          const target = event.target as HTMLElement;
          const isInsideDialog =
            target.closest('[role="dialog"]') !== null ||
            target.closest(".MuiDialog-root") !== null ||
            target.closest(".MuiBackdrop-root") !== null;

          if (isInsideDialog) {
            return;
          }

          handleClose();
        },
        [handleClose]
      );

      // Expose methods through ref
      useImperativeHandle(
        ref,
        () => ({
          closePopover: handleClose,
        }),
        [handleClose]
      );

      const handleOnClear = useCallback(() => {
        popoverOptions?.onClearButtonClick &&
          popoverOptions.onClearButtonClick();
      }, [popoverOptions]);

      const handleOnCancel = useCallback(() => {
        popoverOptions?.onCancelButtonClick &&
          popoverOptions.onCancelButtonClick();
        setAnchorEl(null);
      }, [popoverOptions]);

      const handleOnApply = useCallback(() => {
        popoverOptions?.onApplyButtonClick &&
          popoverOptions.onApplyButtonClick();
        setAnchorEl(null);
      }, [popoverOptions]);

      const selectedCount = useMemo(
        () => getSelectedCount(),
        [getSelectedCount]
      );

      // Determine the label based on the number of selected items
      const chipLabel = useMemo(() => {
        if (renderLabel) {
          return renderLabel(label, selectedCount);
        }

        return renderIncludedCountLabel(label, selectedCount);
      }, [selectedCount, renderLabel, label]);

      const title = useMemo(() => {
        if (popoverOptions?.title) return popoverOptions.title;
        return label;
      }, [popoverOptions?.title, label]);

      // Merge custom paperStyles with default styles
      const mergedPaperStyles = useMemo(() => {
        return paperStyles
          ? { ...defaultPaperStyles, ...paperStyles }
          : defaultPaperStyles;
      }, [paperStyles]);

      // Memoize the popover content to prevent unnecessary re-renders
      const popoverContent = useMemo(() => {
        if (!isOpen) return null; // Don't render content when popover is closed

        return (
          <>
            {title && (
              <PopoverTitle
                title={title}
                addTitleClearButton={addTitleClearButton}
                addTitleCancelButton={addTitleCancelButton}
                titleActions={titleActions}
                titleAdornment={titleAdornment}
                id={id}
                onCancel={handleOnCancel}
                onDelete={onDelete}
              />
            )}
            <Box id={`${id}-content`}>{children}</Box>
            {popoverOptions?.disableActionButtons ? null : (
              <FilterChipActionButtons
                id={`${id}-actions`}
                onClear={handleOnClear}
                onCancel={handleOnCancel}
                onApply={handleOnApply}
                disableClearButton={popoverOptions?.disableClearButton}
                disableCancelButton={popoverOptions?.disableCancelButton}
                disableApplyButton={popoverOptions?.disableApplyButton}
              />
            )}
          </>
        );
      }, [
        isOpen,
        title,
        addTitleClearButton,
        addTitleCancelButton,
        titleActions,
        titleAdornment,
        id,
        onDelete,
        children,
        popoverOptions,
        handleOnClear,
        handleOnCancel,
        handleOnApply,
      ]);

      const renderChip = () => (
        <Chip
          id={`${id}-chip`}
          data-testid={dataTestId}
          label={chipLabel}
          onClick={handleClick}
          variant="outlined"
          onDelete={selectedCount > 0 ? onDelete : undefined}
          sx={{
            zIndex: 1,
            border: highlighted ? "none" : undefined,
          }}
        />
      );

      return (
        <Box sx={{ marginLeft: "0 !important" }}>
          {highlighted ? (
            <RotatingBorder>{renderChip()}</RotatingBorder>
          ) : (
            renderChip()
          )}

          <Popper
            id={`${id}-popper`}
            open={isOpen}
            anchorEl={anchorEl}
            placement="bottom-start"
            modifiers={[{ name: "hide", enabled: true }]}
            sx={{
              zIndex: 1300,
              "&[data-popper-reference-hidden]": {
                visibility: "hidden",
              },
            }}
          >
            <ClickAwayListener
              onClickAway={handleClickAway}
              mouseEvent="onMouseUp"
            >
              <Paper sx={mergedPaperStyles}>{popoverContent}</Paper>
            </ClickAwayListener>
          </Popper>
        </Box>
      );
    }
  )
);
